import React, { useState, useEffect, useCallback } from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { Button } from "primereact/button";
import { Tooltip } from "@mui/material";
import { saveSectionData, loadSectionData, updateStep4WithSectionA } from "../utils/answerManager";

/** Normalize country for national vs international comparison. */
const normalizeCountryName = (s) =>
  String(s || "")
    .trim()
    .toLowerCase();

const getEntityCountryField = (entity) => {
  if (!entity || typeof entity !== "object") return "";
  return (
    entity.country ||
    entity.Country ||
    entity.registeredCountry ||
    entity.countryName ||
    ""
  );
};

/** True if site is allocated to the legal entity (list-sitelocation: legalEntityMapping). */
const siteBelongsToLegalEntity = (site, entityName) => {
  if (!site || !entityName) return false;
  const map = site.legalEntityMapping;
  if (!map || typeof map !== "object") return false;
  const target = String(entityName).trim().toLowerCase();
  return Object.keys(map).some((k) => String(k).trim().toLowerCase() === target);
};

const filterSitesByLegalEntity = (sites, entity) => {
  if (!Array.isArray(sites) || !entity?.name) return [];
  return sites.filter((s) => siteBelongsToLegalEntity(s, entity.name));
};

/**
 * Plant = Manufacturing only. Offices = Warehouse | Office | Store (site creation dropdown).
 * Other types are excluded from Q18 counts.
 */
const classifySiteTypeCategory = (siteTypeRaw) => {
  const t = String(siteTypeRaw || "").trim().toLowerCase();
  if (t === "manufacturing") return "plant";
  if (t === "warehouse" || t === "office" || t === "store") return "office";
  return "other";
};

/** If API omits entity.country, use the mode of countries among that entity's sites (e.g. all India → India). */
const resolveReportingHomeCountry = (entity, entitySites) => {
  const fromEntity = getEntityCountryField(entity);
  if (fromEntity) return String(fromEntity).trim();
  if (!entitySites?.length) return "";
  const counts = {};
  entitySites.forEach((s) => {
    const n = normalizeCountryName(s.country);
    if (n) counts[n] = (counts[n] || 0) + 1;
  });
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (sorted.length === 0) return "";
  const topNorm = sorted[0][0];
  const sample = entitySites.find((s) => normalizeCountryName(s.country) === topNorm);
  return sample?.country != null ? String(sample.country).trim() : "";
};

const countriesMatchForNational = (siteCountry, homeCountry) => {
  const hc = normalizeCountryName(homeCountry);
  if (!hc) return false;
  return normalizeCountryName(siteCountry) === hc;
};

/** Q18 + Q19 aggregates from list-sitelocation rows filtered by reporting legal entity. */
const computeLocationAggregates = (sites, entity) => {
  const scoped = filterSitesByLegalEntity(sites, entity);
  if (!scoped.length) {
    return {
      plantsNational: 0,
      plantsInternational: 0,
      officesNational: 0,
      officesInternational: 0,
      nationalLocations: 0,
      internationalLocations: 0,
    };
  }
  const homeCountry = resolveReportingHomeCountry(entity, scoped);
  let plantsNational = 0;
  let plantsInternational = 0;
  let officesNational = 0;
  let officesInternational = 0;
  const nationalStates = new Set();
  const internationalCountries = new Set();

  scoped.forEach((site) => {
    const cat = classifySiteTypeCategory(site.siteType);
    if (cat === "plant") {
      if (countriesMatchForNational(site.country, homeCountry)) plantsNational += 1;
      else plantsInternational += 1;
    } else if (cat === "office") {
      if (countriesMatchForNational(site.country, homeCountry)) officesNational += 1;
      else officesInternational += 1;
    }

    const siteCountry = site.country || "";
    const siteState = site.state || "";
    if (countriesMatchForNational(siteCountry, homeCountry)) {
      if (siteState) nationalStates.add(String(siteState).trim());
    } else if (siteCountry) {
      internationalCountries.add(String(siteCountry).trim());
    }
  });

  return {
    plantsNational,
    plantsInternational,
    officesNational,
    officesInternational,
    nationalLocations: nationalStates.size,
    internationalLocations: internationalCountries.size,
  };
};

const SectionA = ({ reportId }) => {
  const username = localStorage.getItem("userName");
  const companyid = localStorage.getItem("companyid");

  // Use reportId from props or fallback to localStorage
  const storageReportId = reportId || localStorage.getItem("brsr_report_id");

  const [formData, setFormData] = useState({
    cin: "",
    name: "",
    yearOfIncorporation: "",
    registeredOfficeAddress: "",
    corporateAddress: "",
    email: "",
    telephoneCountryCode: "",
    telephoneNumber: "",
    website: "",
    financialYear: "",
    stockExchange: "",
    paidUpCapitalCurrency: "",
    paidUpCapitalAmount: "",
    contactPersonName: "",
    contactPersonTelephone: "",
    contactPersonEmail: "",
    reportingBoundary: "", // "standalone" or "consolidated"
    assuranceProviderName: "",
    assuranceType: "", // Text input now
  });

  const [legalEntityData, setLegalEntityData] = useState(null);
  const [selectedLegalEntities, setSelectedLegalEntities] = useState([]);
  const [listedEntityOptions, setListedEntityOptions] = useState([]);
  const [siteLocations, setSiteLocations] = useState([]);
  const [siteLocationsRaw, setSiteLocationsRaw] = useState([]); // Store raw site location data
  const [countryCodeOptions, setCountryCodeOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cinError, setCinError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [contactPersonEmailError, setContactPersonEmailError] = useState("");
  const [websiteError, setWebsiteError] = useState("");
  const [nameError, setNameError] = useState("");
  const [contactPersonNameError, setContactPersonNameError] = useState("");
  const [assuranceProviderNameError, setAssuranceProviderNameError] = useState("");

  // Business Activities Table State
  const [businessActivities, setBusinessActivities] = useState([{ id: 1, mainActivity: "", businessActivity: "", turnoverPercent: "" }]);

  // Products/Services Table State
  const [productsServices, setProductsServices] = useState([{ id: 1, productService: "", nicCode: "", turnoverPercent: "" }]);

  // Operations State
  const [operationsData, setOperationsData] = useState({
    // Manual inputs for locations (if sites not configured)
    plantsNational: "",
    plantsInternational: "",
    officesNational: "",
    officesInternational: "",
    // Markets served
    exportsPercentage: "",
    customerTypes: "",
  });

  // Employees State
  const [employeesData, setEmployeesData] = useState({
    // Employees - Permanent
    empPermanentTotal: "",
    empPermanentMale: "",
    empPermanentFemale: "",
    // Employees - Other than Permanent
    empOtherTotal: "",
    empOtherMale: "",
    empOtherFemale: "",
    // Workers - Permanent
    workerPermanentTotal: "",
    workerPermanentMale: "",
    workerPermanentFemale: "",
    // Workers - Other than Permanent
    workerOtherTotal: "",
    workerOtherMale: "",
    workerOtherFemale: "",
    // Differently abled - Employees Permanent
    diffAbledEmpPermanentTotal: "",
    diffAbledEmpPermanentMale: "",
    diffAbledEmpPermanentFemale: "",
    // Differently abled - Employees Other
    diffAbledEmpOtherTotal: "",
    diffAbledEmpOtherMale: "",
    diffAbledEmpOtherFemale: "",
    // Differently abled - Workers Permanent
    diffAbledWorkerPermanentTotal: "",
    diffAbledWorkerPermanentMale: "",
    diffAbledWorkerPermanentFemale: "",
    // Differently abled - Workers Other
    diffAbledWorkerOtherTotal: "",
    diffAbledWorkerOtherMale: "",
    diffAbledWorkerOtherFemale: "",
    // Women participation
    womenBoardTotal: "",
    womenBoardCount: "",
    womenSeniorTotal: "",
    womenSeniorCount: "",
    womenMiddleTotal: "",
    womenMiddleCount: "",
    womenJuniorTotal: "",
    womenJuniorCount: "",
    // Turnover rates - Current FY
    turnoverEmpCurrentMale: "",
    turnoverEmpCurrentFemale: "",
    turnoverWorkerCurrentMale: "",
    turnoverWorkerCurrentFemale: "",
    // Turnover rates - Previous FY
    turnoverEmpPreviousMale: "",
    turnoverEmpPreviousFemale: "",
    turnoverWorkerPreviousMale: "",
    turnoverWorkerPreviousFemale: "",
    // Turnover rates - Prior to Previous FY
    turnoverEmpPriorMale: "",
    turnoverEmpPriorFemale: "",
    turnoverWorkerPriorMale: "",
    turnoverWorkerPriorFemale: "",
  });

  // Holding/Subsidiary Companies State
  const [holdingCompanies, setHoldingCompanies] = useState([{ id: 1, name: "", type: "", sharePercentage: "", participates: "" }]);

  // CSR State
  const [csrData, setCsrData] = useState({
    csrApplicable: "",
    csrPolicyWebLink: "",
    turnover: "",
    netWorth: "",
  });

  // Complaints/Grievances State
  const [complaintsData, setComplaintsData] = useState({
    communities: { mechanism: "", webLink: "", currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
    investors: { mechanism: "", webLink: "", currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
    shareholders: { mechanism: "", webLink: "", currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
    employees: { mechanism: "", webLink: "", currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
    customers: { mechanism: "", webLink: "", currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
    valueChain: { mechanism: "", webLink: "", currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
    other: { mechanism: "", webLink: "", currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "", otherSpecify: "" },
  });

  // Material Issues State
  const [materialIssues, setMaterialIssues] = useState([{ id: 1, issue: "", type: "", rationale: "", approach: "", financialImplications: "" }]);

  // Prefill Section A when editing an existing BRSR report.
  useEffect(() => {
    try {
      // First try to load from report-specific storage
      const saved = loadSectionData(storageReportId, "A");
      if (saved) {
        if (saved.formData) {
          setFormData((prev) => ({ ...prev, ...saved.formData }));
        }
        if (saved.businessActivities && saved.businessActivities.length > 0) {
          setBusinessActivities(saved.businessActivities);
        }
        if (saved.productsServices && saved.productsServices.length > 0) {
          setProductsServices(saved.productsServices);
        }
        if (saved.operationsData) {
          setOperationsData((prev) => ({ ...prev, ...saved.operationsData }));
        }
        if (saved.employeesData) {
          setEmployeesData((prev) => ({ ...prev, ...saved.employeesData }));
        }
        if (saved.holdingCompanies && saved.holdingCompanies.length > 0) {
          setHoldingCompanies(saved.holdingCompanies);
        }
        if (saved.csrData) {
          setCsrData((prev) => ({ ...prev, ...saved.csrData }));
        }
        if (saved.complaintsData) {
          setComplaintsData((prev) => ({ ...prev, ...saved.complaintsData }));
        }
        if (saved.materialIssues && saved.materialIssues.length > 0) {
          setMaterialIssues(saved.materialIssues);
        }
      }
    } catch (e) {
      console.error("Failed to load Section A data:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageReportId]);

  /** Legal entity used for site filters (dropdown + list-entities row). */
  const getReportingLegalEntity = () => {
    if (legalEntityData?.name) return legalEntityData;
    if (formData.name && Array.isArray(selectedLegalEntities)) {
      const found = selectedLegalEntities.find(
        (e) =>
          String(e?.name || "").trim().toLowerCase() === String(formData.name).trim().toLowerCase(),
      );
      return found || null;
    }
    return null;
  };

  // Auto-save Section A data whenever any form data changes
  // Using useCallback to memoize the save function
  const saveAllSectionData = useCallback(() => {
    try {
      let operationsPayload = { ...operationsData };
      const reportingEntity = getReportingLegalEntity();
      if (siteLocationsRaw?.length > 0 && reportingEntity) {
        const agg = computeLocationAggregates(siteLocationsRaw, reportingEntity);
        operationsPayload = {
          ...operationsData,
          ...agg,
          _locationsAutoPopulated: true,
        };
      }

      const dataToSave = {
        formData,
        businessActivities,
        productsServices,
        operationsData: operationsPayload,
        employeesData,
        holdingCompanies,
        csrData,
        complaintsData,
        materialIssues,
      };

      // Save to report-specific localStorage
      saveSectionData(storageReportId, "A", dataToSave);

      // Also update step4 for PDF generation
      updateStep4WithSectionA(storageReportId, dataToSave);
    } catch (e) {
      console.error("Failed to save Section A data:", e);
    }
  }, [
    formData,
    businessActivities,
    productsServices,
    operationsData,
    employeesData,
    holdingCompanies,
    csrData,
    complaintsData,
    materialIssues,
    storageReportId,
    siteLocationsRaw,
    legalEntityData,
    selectedLegalEntities,
  ]);

  // Debounced auto-save effect
  useEffect(() => {
    // Debounce the save to avoid too frequent saves
    const timeoutId = setTimeout(() => {
      saveAllSectionData();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [saveAllSectionData]);

  const reportingBoundaryOptions = [
    { label: "Standalone", value: "standalone" },
    { label: "Consolidated", value: "consolidated" },
  ];

  // Helper function to validate numbers only (no letters or special characters except decimal point)
  const validateNumbersOnly = (value) => {
    if (value === "") return true;
    // Allow only digits and one decimal point
    return /^\d*\.?\d*$/.test(value);
  };

  // Helper function to validate integers only (no decimals, no letters)
  const validateIntegersOnly = (value) => {
    if (value === "") return true;
    // Allow only digits
    return /^\d+$/.test(value);
  };

  // Helper function to validate CIN format
  // CIN format: [Classification(1)][Sub-category(5)][State(2)][Year(4)][Company Type(3)][Company Number(6)]
  // Example: U72900DL2015PTC123456
  const validateCIN = (value) => {
    if (value === "") return { isValid: true, error: "" };

    // Remove spaces and convert to uppercase
    const cleanedValue = value.replace(/\s/g, "").toUpperCase();

    // Check length
    if (cleanedValue.length > 21) {
      return { isValid: false, error: "CIN must be exactly 21 characters" };
    }

    // Check if all characters are alphanumeric
    if (!/^[A-Z0-9]+$/.test(cleanedValue)) {
      return { isValid: false, error: "CIN can only contain uppercase letters and numbers" };
    }

    // If length is 21, validate the format pattern
    if (cleanedValue.length === 21) {
      // Pattern: [1 letter][5 digits][2 letters][4 digits][3 letters][6 alphanumeric]
      const cinPattern = /^[A-Z][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[A-Z0-9]{6}$/;
      if (!cinPattern.test(cleanedValue)) {
        return {
          isValid: false,
          error: "Invalid CIN format. Expected format: [Letter][5 digits][2 letters][4 digits][3 letters][6 alphanumeric]",
        };
      }
    }

    return { isValid: true, error: "", cleanedValue };
  };

  // Helper function to validate email format
  const validateEmail = (value) => {
    if (value === "") return { isValid: true, error: "" };

    // Standard email regex pattern
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!emailPattern.test(value)) {
      return { isValid: false, error: "Please enter a valid email address (e.g., example@domain.com)" };
    }

    return { isValid: true, error: "" };
  };

  // Helper function to validate URL format
  const validateURL = (value) => {
    if (value === "") return { isValid: true, error: "" };

    // URL regex pattern - allows http://, https://, www., or just domain
    const urlPattern = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;

    if (!urlPattern.test(value)) {
      return { isValid: false, error: "Please enter a valid URL (e.g., https://www.example.com or www.example.com)" };
    }

    return { isValid: true, error: "" };
  };

  // Helper function to filter out numbers from name input
  const filterNameInput = (value) => {
    // Remove all digits (0-9) from the input
    return value.replace(/[0-9]/g, "");
  };

  // Helper function to validate name format
  const validateName = (value, fieldName = "Name") => {
    if (value === "") return { isValid: true, error: "" };

    // Trim whitespace
    const trimmedValue = value.trim();

    // Check minimum length
    if (trimmedValue.length < 2) {
      return { isValid: false, error: `${fieldName} must be at least 2 characters long` };
    }

    // Check maximum length (reasonable limit)
    if (trimmedValue.length > 100) {
      return { isValid: false, error: `${fieldName} must not exceed 100 characters` };
    }

    // Allow letters, spaces, hyphens, apostrophes, and periods (for names like "Dr. John O'Brien-Smith")
    const namePattern = /^[a-zA-Z\s.'-]+$/;

    if (!namePattern.test(trimmedValue)) {
      return { isValid: false, error: `${fieldName} can only contain letters, spaces, hyphens, apostrophes, and periods` };
    }

    // Check for consecutive spaces
    if (/\s{2,}/.test(trimmedValue)) {
      return { isValid: false, error: `${fieldName} cannot contain consecutive spaces` };
    }

    // Check that it doesn't start or end with special characters
    if (/^[.\s'-]|[.\s'-]$/.test(trimmedValue)) {
      return { isValid: false, error: `${fieldName} cannot start or end with spaces or special characters` };
    }

    return { isValid: true, error: "", cleanedValue: trimmedValue };
  };

  // Contact person names should allow periods and spaces naturally while typing (e.g., "Mr. A. K. Sharma").
  const validateContactPersonName = (value) => {
    if (value === "") return { isValid: true, error: "" };
    const trimmedValue = value.trim();
    if (trimmedValue.length < 2) {
      return { isValid: false, error: "Contact Person Name must be at least 2 characters long" };
    }
    if (trimmedValue.length > 100) {
      return { isValid: false, error: "Contact Person Name must not exceed 100 characters" };
    }
    const namePattern = /^[a-zA-Z\s.'-]+$/;
    if (!namePattern.test(trimmedValue)) {
      return { isValid: false, error: "Contact Person Name can only contain letters, spaces, hyphens, apostrophes, and periods" };
    }
    if (/\s{2,}/.test(trimmedValue)) {
      return { isValid: false, error: "Contact Person Name cannot contain consecutive spaces" };
    }
    return { isValid: true, error: "", cleanedValue: trimmedValue };
  };

  const validateMinLengthOnly = (value, fieldName, minLength = 2) => {
    if (value === "") return { isValid: true, error: "" };
    const trimmedValue = String(value).trim();
    if (trimmedValue.length < minLength) {
      return { isValid: false, error: `${fieldName} must be at least ${minLength} characters long` };
    }
    return { isValid: true, error: "", cleanedValue: trimmedValue };
  };

  // Fetch Financial Year from step1
  useEffect(() => {
    const step1 = localStorage.getItem("step1");
    if (step1) {
      try {
        const dataStep1 = JSON.parse(step1);
        if (dataStep1.financialYear) {
          setFormData((prev) => ({
            ...prev,
            financialYear: dataStep1.financialYear,
          }));
        }
      } catch (error) {
        console.error("Error parsing step1:", error);
      }
    }
  }, []);

  // Fetch Legal Entity Data
  useEffect(() => {
    const fetchLegalEntity = async () => {
      try {
        const myHeaders = new Headers();
        myHeaders.append("username", username);
        const requestOptions = {
          method: "GET",
          headers: myHeaders,
          redirect: "follow",
        };

        const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}list-entities`, requestOptions);
        if (response.status === 200) {
          const responseJson = await response.json();
          if (responseJson.success && responseJson.data) {
            const activeEntities = responseJson.data.filter((item) => item.isActive === true);
            if (activeEntities.length > 0) {
              // Read legal entities selected in New Report (step1).
              const step1Raw = localStorage.getItem("step1");
              let selectedEntityNames = [];
              if (step1Raw) {
                try {
                  const step1 = JSON.parse(step1Raw);
                  if (Array.isArray(step1?.legalEntity)) {
                    selectedEntityNames = step1.legalEntity.map((item) => String(item || "").trim()).filter(Boolean);
                  } else {
                    selectedEntityNames = String(step1?.legalEntity || "")
                      .split(",")
                      .map((item) => item.trim())
                      .filter(Boolean);
                  }
                } catch (e) {
                  // Ignore parse issues and fallback below
                }
              }

              // Keep only active entities that were selected in step1.
              const matchedEntities =
                selectedEntityNames.length > 0
                  ? activeEntities.filter((entity) =>
                      selectedEntityNames.some(
                        (name) =>
                          String(entity?.name || "")
                            .trim()
                            .toLowerCase() === name.toLowerCase(),
                      ),
                    )
                  : activeEntities;

              setSelectedLegalEntities(matchedEntities);
              setListedEntityOptions(
                matchedEntities.map((entity) => ({
                  label: entity.name,
                  value: entity.name,
                })),
              );

              // Auto-select when there is exactly one configured option.
              if (matchedEntities.length === 1) {
                const entityToUse = matchedEntities[0];
                setLegalEntityData(entityToUse);
                setFormData((prev) => ({
                  ...prev,
                  name: entityToUse?.name || "",
                }));
              } else if (matchedEntities.length > 1) {
                // Keep already selected value when valid, otherwise select first option.
                setFormData((prev) => {
                  const existingSelection = String(prev.name || "")
                    .trim()
                    .toLowerCase();
                  const hasExistingSelection = matchedEntities.some(
                    (entity) =>
                      String(entity?.name || "")
                        .trim()
                        .toLowerCase() === existingSelection,
                  );
                  const nextName = hasExistingSelection ? prev.name : matchedEntities[0]?.name || "";
                  const selectedEntity =
                    matchedEntities.find(
                      (entity) =>
                        String(entity?.name || "")
                          .trim()
                          .toLowerCase() ===
                        String(nextName || "")
                          .trim()
                          .toLowerCase(),
                    ) || null;
                  setLegalEntityData(selectedEntity);
                  return {
                    ...prev,
                    name: nextName,
                  };
                });
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching legal entity:", error);
      }
    };

    fetchLegalEntity();
  }, [username]);

  // Fetch Currencies from API
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}currencies`);
        if (response.ok) {
          const data = await response.json();
          if (data && Array.isArray(data)) {
            // If API returns array of currency objects
            const options = data.map((item) => ({
              label: item.code ? `${item.code}${item.symbol ? ` (${item.symbol})` : ""}` : item.name || item,
              value: item.code || item.value || item,
            }));
            setCurrencyOptions(options);
          } else if (data.data && Array.isArray(data.data)) {
            // If API returns { data: [...] }
            const options = data.data.map((item) => ({
              label: item.code ? `${item.code}${item.symbol ? ` (${item.symbol})` : ""}` : item.name || item,
              value: item.code || item.value || item,
            }));
            setCurrencyOptions(options);
          }
        }
      } catch (error) {
        console.error("Error fetching currencies:", error);
        // Fallback to default currencies if API fails
        setCurrencyOptions([
          { label: "INR (₹)", value: "INR" },
          { label: "USD ($)", value: "USD" },
          { label: "EUR (€)", value: "EUR" },
          { label: "GBP (£)", value: "GBP" },
          { label: "JPY (¥)", value: "JPY" },
        ]);
      }
    };

    fetchCurrencies();
  }, []);

  // Fetch Site Locations
  useEffect(() => {
    const fetchSiteLocations = async () => {
      try {
        const myHeaders = new Headers();
        myHeaders.append("username", username);
        const requestOptions = {
          method: "GET",
          headers: myHeaders,
          redirect: "follow",
        };

        const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}list-sitelocation`, requestOptions);
        if (response.status === 200) {
          const responseJson = await response.json();
          if (responseJson.success && responseJson.data) {
            const activeLocations = responseJson.data.filter((item) => item.isActive === true);
            // Store raw data for calculations
            setSiteLocationsRaw(activeLocations);
            // Compose full address from site location config fields (no single address field exists)
            const buildAddress = (location) =>
              [location.addressLine1, location.addressLine2, location.city, location.state, location.postalCode, location.country]
                .filter((part) => part && String(part).trim())
                .join(", ");
            // Map site locations to dropdown options; value = composed address so report prints address, not name
            const locationOptions = activeLocations.map((location) => {
              const composedAddress = buildAddress(location) || location.name || `Location ${location.id}`;
              return { label: composedAddress, value: composedAddress };
            });
            setSiteLocations(locationOptions);
          }
        }
      } catch (error) {
        console.error("Error fetching site locations:", error);
      }
    };

    fetchSiteLocations();
  }, [username]);

  // Fetch Country Codes from API
  useEffect(() => {
    const fetchCountryCodes = async () => {
      try {
        // Try to fetch from backend API first
        const myHeaders = new Headers();
        myHeaders.append("username", username);
        const requestOptions = {
          method: "GET",
          headers: myHeaders,
          redirect: "follow",
        };

        // Check if there's a backend API for country codes
        // If not, use REST Countries API as fallback
        try {
          const backendResponse = await fetch(`${process.env.REACT_APP_BASE_API_URL}country-codes`, requestOptions);
          if (backendResponse.status === 200) {
            const backendData = await backendResponse.json();
            if (backendData && Array.isArray(backendData)) {
              // Backend API returns country codes
              const options = backendData.map((item) => ({
                label: `${item.code} (${item.name})`,
                value: item.code,
              }));
              setCountryCodeOptions(options);
              return;
            }
          }
        } catch (backendError) {
          console.log("Backend country codes API not available, using REST Countries API");
        }

        // Fallback to REST Countries API
        const response = await fetch("https://restcountries.com/v3.1/all?fields=name,idd");
        if (response.ok) {
          const countries = await response.json();
          const codes = [];

          countries.forEach((country) => {
            if (country.idd && country.idd.root && country.name) {
              const root = country.idd.root;
              const suffixes = country.idd.suffixes || [""];

              suffixes.forEach((suffix) => {
                const fullCode = root + suffix;
                const countryName = country.name.common || country.name.official;
                codes.push({
                  label: `${fullCode} (${countryName})`,
                  value: fullCode,
                  country: countryName,
                });
              });
            }
          });

          // Sort by country name and remove duplicates
          const uniqueCodes = codes.filter((code, index, self) => index === self.findIndex((c) => c.value === code.value)).sort((a, b) => a.country.localeCompare(b.country));

          setCountryCodeOptions(uniqueCodes);
        }
      } catch (error) {
        console.error("Error fetching country codes:", error);
        // Fallback to a basic list if API fails
        setCountryCodeOptions([
          { label: "+1 (US/Canada)", value: "+1" },
          { label: "+44 (UK)", value: "+44" },
          { label: "+91 (India)", value: "+91" },
          { label: "+86 (China)", value: "+86" },
          { label: "+81 (Japan)", value: "+81" },
        ]);
      }
    };

    fetchCountryCodes();
  }, [username]);

  const handleInputChange = (field, value) => {
    if (field === "name" && Array.isArray(selectedLegalEntities) && selectedLegalEntities.length > 0) {
      const selectedEntity = selectedLegalEntities.find(
        (entity) =>
          String(entity?.name || "")
            .trim()
            .toLowerCase() ===
          String(value || "")
            .trim()
            .toLowerCase(),
      );
      setLegalEntityData(selectedEntity || null);
    }
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const tableRows = [
    {
      id: 1,
      label: "Corporate Identity Number (CIN) of the Listed Entity",
      field: "cin",
      type: "cin",
      maxLength: 21,
    },
    {
      id: 2,
      label: "Name of the Listed Entity",
      field: "name",
      type: "autopopulated-text",
      autoPopulateFrom: "legalEntity",
    },
    {
      id: 3,
      label: "Year of incorporation",
      field: "yearOfIncorporation",
      type: "year-picker",
    },
    {
      id: 4,
      label: "Registered office address",
      field: "registeredOfficeAddress",
      type: "address-dropdown",
    },
    {
      id: 5,
      label: "Corporate address",
      field: "corporateAddress",
      type: "address-dropdown",
    },
    {
      id: 6,
      label: "E-mail",
      field: "email",
      type: "email",
    },
    {
      id: 7,
      label: "Telephone",
      field: "telephone",
      type: "telephone",
    },
    {
      id: 8,
      label: "Website",
      field: "website",
      type: "website",
    },
    {
      id: 9,
      label: "Financial year for which reporting is being done",
      field: "financialYear",
      type: "autopopulated-financial-year",
    },
    {
      id: 10,
      label: "Name of the Stock Exchange(s) where shares are listed",
      field: "stockExchange",
      type: "text",
    },
    {
      id: 11,
      label: "Paid-up Capital",
      field: "paidUpCapital",
      type: "paid-up-capital",
    },
    {
      id: 12,
      label: "Name and contact details (telephone, email address) of the person who may be contacted in case of any queries on the BRSR report",
      field: "contactPerson",
      type: "contact-person",
    },
    {
      id: 13,
      label: "Reporting boundary - Are the disclosures under this report made on a standalone basis (i.e. only for the entity) or on a consolidated basis (i.e. for the entity and all the entities which form a part of its consolidated financial statements, taken together).",
      field: "reportingBoundary",
      type: "reporting-boundary",
    },
    {
      id: 14,
      label: "Name of assurance provider",
      field: "assuranceProviderName",
      type: "text",
    },
    {
      id: 15,
      label: "Type of assurance obtained",
      field: "assuranceType",
      type: "text",
    },
  ];

  const renderInputField = (row) => {
    switch (row.type) {
      case "cin":
        return (
          <div>
            <InputText
              value={formData[row.field] || ""}
              onChange={(e) => {
                const value = e.target.value;
                // Only allow alphanumeric characters, convert to uppercase
                const cleanedValue = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

                if (cleanedValue.length <= row.maxLength) {
                  const validation = validateCIN(cleanedValue);
                  if (validation.isValid) {
                    handleInputChange(row.field, cleanedValue);
                    setCinError("");
                  } else {
                    // Still allow typing but show error
                    handleInputChange(row.field, cleanedValue);
                    setCinError(validation.error);
                  }
                }
              }}
              onBlur={(e) => {
                const value = e.target.value;
                const validation = validateCIN(value);
                if (!validation.isValid) {
                  setCinError(validation.error);
                } else if (value.length > 0 && value.length < 21) {
                  setCinError("CIN must be exactly 21 characters");
                } else {
                  setCinError("");
                }
              }}
              className={`section-a-input ${cinError ? "section-a-input-error" : ""}`}
              placeholder="Enter CIN (e.g., U72900DL2015PTC123456)"
              maxLength={row.maxLength}
              style={{ textTransform: "uppercase" }}
            />
            {cinError && (
              <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                {cinError}
              </div>
            )}
            {!cinError && formData[row.field] && formData[row.field].length > 0 && formData[row.field].length < 21 && <div style={{ marginTop: "4px", fontSize: "12px", color: "#666" }}>{21 - formData[row.field].length} characters remaining</div>}
            {!cinError && formData[row.field] && formData[row.field].length === 21 && <div style={{ marginTop: "4px", fontSize: "12px", color: "#2e7d32" }}>✓ Valid CIN format</div>}
          </div>
        );
      case "autopopulated-text":
        // Name of listed entity should come from selected legal entities in step1.
        if (row.field === "name" && listedEntityOptions.length > 1) {
          return <Dropdown value={formData[row.field] || ""} options={listedEntityOptions} onChange={(e) => handleInputChange(row.field, e.value || "")} className="section-a-dropdown" placeholder="Select Name of the Listed Entity" />;
        }

        if (legalEntityData && legalEntityData.name) {
          return <InputText value={formData[row.field] || ""} disabled className="section-a-input" placeholder="Auto-populated from Legal Entity" />;
        } else {
          // If it's the name field, add validation
          if (row.field === "name") {
            return (
              <div>
                <InputText
                  value={formData[row.field] || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Filter out numbers before processing
                    const filteredValue = filterNameInput(value);
                    const validation = validateName(filteredValue, row.label);
                    if (validation.isValid) {
                      handleInputChange(row.field, validation.cleanedValue || filteredValue);
                      setNameError("");
                    } else {
                      handleInputChange(row.field, filteredValue);
                      setNameError(validation.error);
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    const validation = validateName(value, row.label);
                    if (!validation.isValid) {
                      setNameError(validation.error);
                    } else {
                      setNameError("");
                    }
                  }}
                  className={`section-a-input ${nameError ? "section-a-input-error" : ""}`}
                  placeholder="Enter Name of the Listed Entity"
                />
                {nameError && (
                  <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                    {nameError}
                  </div>
                )}
              </div>
            );
          }
          return <InputText value={formData[row.field] || ""} onChange={(e) => handleInputChange(row.field, e.target.value)} className="section-a-input" placeholder="Enter Name of the Listed Entity" />;
        }
      case "year-picker":
        // Generate year options from 1900 to 2100
        const currentYear = new Date().getFullYear();
        const startYear = 1900;
        const endYear = 2100;
        const yearOptions = [];
        for (let year = endYear; year >= startYear; year--) {
          yearOptions.push({ label: year.toString(), value: year.toString() });
        }

        return <Dropdown value={formData[row.field]} options={yearOptions} onChange={(e) => handleInputChange(row.field, e.value || "")} placeholder="Select Year (YYYY)" className="section-a-dropdown" filter filterPlaceholder="Search year" />;
      case "address-dropdown":
        // If site locations exist, show dropdown
        // Otherwise, show text input
        if (siteLocations.length > 0) {
          return <Dropdown value={formData[row.field]} options={siteLocations} onChange={(e) => handleInputChange(row.field, e.value)} placeholder="Select address from Site Locations" className="section-a-dropdown" filter />;
        } else {
          return <InputText value={formData[row.field] || ""} onChange={(e) => handleInputChange(row.field, e.target.value)} className="section-a-input" placeholder="Enter address manually" />;
        }
      case "text":
        // Check if it's a name field
        if (row.field === "name" || row.field === "assuranceProviderName") {
          const errorState = row.field === "name" ? nameError : assuranceProviderNameError;
          const setErrorState = row.field === "name" ? setNameError : setAssuranceProviderNameError;
          return (
            <div>
              <InputText
                value={formData[row.field] || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (row.field === "assuranceProviderName") {
                    const validation = validateMinLengthOnly(value, row.label, 2);
                    handleInputChange(row.field, value);
                    if (validation.isValid) setErrorState("");
                    else setErrorState(validation.error);
                    return;
                  }

                  // Filter out numbers before processing for strict name fields
                  const filteredValue = filterNameInput(value);
                  const validation = validateName(filteredValue, row.label);
                  if (validation.isValid) {
                    handleInputChange(row.field, validation.cleanedValue || filteredValue);
                    setErrorState("");
                  } else {
                    handleInputChange(row.field, filteredValue);
                    setErrorState(validation.error);
                  }
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  const validation = row.field === "assuranceProviderName" ? validateMinLengthOnly(value, row.label, 2) : validateName(value, row.label);
                  if (!validation.isValid) {
                    setErrorState(validation.error);
                  } else {
                    if (row.field === "assuranceProviderName") {
                      handleInputChange(row.field, validation.cleanedValue || value);
                    }
                    setErrorState("");
                  }
                }}
                className={`section-a-input ${errorState ? "section-a-input-error" : ""}`}
                placeholder={row.field === "assuranceProviderName" ? "Enter name of assurance provider" : "Enter name"}
              />
              {errorState && (
                <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                  {errorState}
                </div>
              )}
            </div>
          );
        }
        // Set appropriate placeholder based on field
        let placeholderText = "Enter value";
        if (row.field === "stockExchange") {
          placeholderText = "Enter one stock exchange per line (e.g., NSE, BSE)";
          return <InputTextarea value={formData[row.field] || ""} onChange={(e) => handleInputChange(row.field, e.target.value)} className="section-a-input" placeholder={placeholderText} autoResize rows={3} />;
        } else if (row.field === "assuranceType") {
          placeholderText = "Enter type of assurance (e.g., Limited Assurance, Reasonable Assurance)";
        }
        return <InputText value={formData[row.field] || ""} onChange={(e) => handleInputChange(row.field, e.target.value)} className="section-a-input" placeholder={placeholderText} type="text" />;
      case "email":
        return (
          <div>
            <InputText
              value={formData[row.field] || ""}
              onChange={(e) => {
                const value = e.target.value;
                const validation = validateEmail(value);
                if (validation.isValid) {
                  handleInputChange(row.field, value);
                  setEmailError("");
                } else {
                  handleInputChange(row.field, value);
                  setEmailError(validation.error);
                }
              }}
              onBlur={(e) => {
                const value = e.target.value;
                const validation = validateEmail(value);
                if (!validation.isValid) {
                  setEmailError(validation.error);
                } else {
                  setEmailError("");
                }
              }}
              className={`section-a-input ${emailError ? "section-a-input-error" : ""}`}
              placeholder="Enter email address"
              type="email"
            />
            {emailError && (
              <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                {emailError}
              </div>
            )}
          </div>
        );
      case "dropdown":
        return <Dropdown value={formData[row.field]} options={row.options} onChange={(e) => handleInputChange(row.field, e.value)} placeholder="Select option" className="section-a-dropdown" />;
      case "radio":
        return (
          <div className="section-a-radio-group">
            {row.options.map((option) => (
              <div key={option.value} className="section-a-radio-item">
                <RadioButton inputId={`${row.field}-${option.value}`} value={option.value} checked={formData[row.field] === option.value} onChange={(e) => handleInputChange(row.field, e.value)} />
                <label htmlFor={`${row.field}-${option.value}`} className="section-a-radio-label">
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        );
      case "telephone":
        return (
          <div className="section-a-telephone-group">
            <Dropdown value={formData.telephoneCountryCode} options={countryCodeOptions} onChange={(e) => handleInputChange("telephoneCountryCode", e.value || "")} placeholder="Country Code" className="section-a-dropdown section-a-country-code" style={{ width: "40%", marginRight: "8px" }} filter filterPlaceholder="Search country code" />
            <InputText
              value={formData.telephoneNumber || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (validateIntegersOnly(value)) {
                  handleInputChange("telephoneNumber", value);
                }
              }}
              className="section-a-input"
              placeholder="Telephone Number"
              style={{ width: "58%" }}
              inputMode="numeric"
            />
          </div>
        );
      case "website":
        return (
          <div>
            <InputText
              value={formData[row.field] || ""}
              onChange={(e) => {
                const value = e.target.value;
                const validation = validateURL(value);
                if (validation.isValid) {
                  handleInputChange(row.field, value);
                  setWebsiteError("");
                } else {
                  handleInputChange(row.field, value);
                  setWebsiteError(validation.error);
                }
              }}
              onBlur={(e) => {
                const value = e.target.value;
                const validation = validateURL(value);
                if (!validation.isValid) {
                  setWebsiteError(validation.error);
                } else {
                  setWebsiteError("");
                }
              }}
              className={`section-a-input ${websiteError ? "section-a-input-error" : ""}`}
              placeholder="Enter website (e.g., https://www.example.com or www.example.com)"
              type="text"
            />
            {websiteError && (
              <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                {websiteError}
              </div>
            )}
          </div>
        );
      case "autopopulated-financial-year":
        return <InputText value={formData[row.field] || ""} disabled className="section-a-input" placeholder="Auto-populated from setup page" />;
      case "paid-up-capital":
        return (
          <div className="section-a-paidup-capital-group">
            <Dropdown value={formData.paidUpCapitalCurrency} options={currencyOptions} onChange={(e) => handleInputChange("paidUpCapitalCurrency", e.value || "")} placeholder="Select Currency" className="section-a-dropdown section-a-currency" style={{ width: "40%", marginRight: "8px" }} filter filterPlaceholder="Search currency" />
            <InputText
              value={formData.paidUpCapitalAmount || ""}
              onChange={(e) => {
                const value = e.target.value;
                // Allow only numbers and decimal point
                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                  handleInputChange("paidUpCapitalAmount", value);
                }
              }}
              className="section-a-input"
              placeholder="Enter paid-up capital amount"
              type="text"
              inputMode="numeric"
              style={{ width: "58%" }}
            />
          </div>
        );
      case "contact-person":
        return (
          <div className="section-a-compound-field">
            <div>
              <InputText
                value={formData.contactPersonName || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  // Filter out numbers before processing
                  const filteredValue = filterNameInput(value);
                  const validation = validateContactPersonName(filteredValue);
                  // Keep user-typed spacing while typing; don't trim here.
                  handleInputChange("contactPersonName", filteredValue);
                  if (validation.isValid) setContactPersonNameError("");
                  else setContactPersonNameError(validation.error);
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  const validation = validateContactPersonName(value);
                  if (!validation.isValid) {
                    setContactPersonNameError(validation.error);
                  } else {
                    // Normalize only on blur so spaces/dots are not blocked during typing.
                    handleInputChange("contactPersonName", validation.cleanedValue || value);
                    setContactPersonNameError("");
                  }
                }}
                className={`section-a-input ${contactPersonNameError ? "section-a-input-error" : ""}`}
                placeholder="Enter contact person name"
                style={{ marginBottom: "8px" }}
              />
              {contactPersonNameError && (
                <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f", marginBottom: "8px" }}>
                  {contactPersonNameError}
                </div>
              )}
            </div>
            <InputText
              value={formData.contactPersonTelephone || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (validateIntegersOnly(value)) {
                  handleInputChange("contactPersonTelephone", value);
                }
              }}
              className="section-a-input"
              placeholder="Enter contact number"
              style={{ marginBottom: "8px" }}
              inputMode="numeric"
            />
            <div>
              <InputText
                value={formData.contactPersonEmail || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  const validation = validateEmail(value);
                  if (validation.isValid) {
                    handleInputChange("contactPersonEmail", value);
                    setContactPersonEmailError("");
                  } else {
                    handleInputChange("contactPersonEmail", value);
                    setContactPersonEmailError(validation.error);
                  }
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  const validation = validateEmail(value);
                  if (!validation.isValid) {
                    setContactPersonEmailError(validation.error);
                  } else {
                    setContactPersonEmailError("");
                  }
                }}
                className={`section-a-input ${contactPersonEmailError ? "section-a-input-error" : ""}`}
                placeholder="Enter contact email address"
                type="email"
              />
              {contactPersonEmailError && (
                <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                  {contactPersonEmailError}
                </div>
              )}
            </div>
          </div>
        );
      case "reporting-boundary":
        return <Dropdown value={formData[row.field]} options={reportingBoundaryOptions} onChange={(e) => handleInputChange(row.field, e.value || "")} placeholder="Select option" className="section-a-dropdown" />;
      case "compound":
        return (
          <div className="section-a-compound-field">
            <InputText value={formData[row.subFields[0]] || ""} onChange={(e) => handleInputChange(row.subFields[0], e.target.value)} className="section-a-input" placeholder="Name" style={{ marginBottom: "8px" }} />
            <InputText value={formData[row.subFields[1]] || ""} onChange={(e) => handleInputChange(row.subFields[1], e.target.value)} className="section-a-input" placeholder="Telephone" style={{ marginBottom: "8px" }} />
            <InputText value={formData[row.subFields[2]] || ""} onChange={(e) => handleInputChange(row.subFields[2], e.target.value)} className="section-a-input" placeholder="Email" />
          </div>
        );
      default:
        return null;
    }
  };

  // Business Activities Functions
  const addBusinessActivityRow = () => {
    const newId = businessActivities.length > 0 ? Math.max(...businessActivities.map((r) => r.id)) + 1 : 1;
    setBusinessActivities([...businessActivities, { id: newId, mainActivity: "", businessActivity: "", turnoverPercent: "" }]);
  };

  const removeBusinessActivityRow = (id) => {
    if (businessActivities.length > 1) {
      setBusinessActivities(businessActivities.filter((row) => row.id !== id));
    }
  };

  const updateBusinessActivity = (id, field, value) => {
    setBusinessActivities(businessActivities.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const calculateBusinessActivitiesTotal = () => {
    return businessActivities.reduce((sum, row) => {
      const percent = parseFloat(row.turnoverPercent) || 0;
      return sum + percent;
    }, 0);
  };

  // Products/Services Functions
  const addProductsServicesRow = () => {
    const newId = productsServices.length > 0 ? Math.max(...productsServices.map((r) => r.id)) + 1 : 1;
    setProductsServices([...productsServices, { id: newId, productService: "", nicCode: "", turnoverPercent: "" }]);
  };

  const removeProductsServicesRow = (id) => {
    if (productsServices.length > 1) {
      setProductsServices(productsServices.filter((row) => row.id !== id));
    }
  };

  const updateProductsServices = (id, field, value) => {
    setProductsServices(productsServices.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const calculateProductsServicesTotal = () => {
    return productsServices.reduce((sum, row) => {
      const percent = parseFloat(row.turnoverPercent) || 0;
      return sum + percent;
    }, 0);
  };

  // Validation for percentage (0-100, max 2 decimals)
  const validatePercentage = (value) => {
    if (value === "") return true;
    const num = parseFloat(value);
    if (isNaN(num)) return false;
    if (num < 0 || num > 100) return false;
    const decimalParts = value.split(".");
    if (decimalParts.length > 1 && decimalParts[1].length > 2) return false;
    return true;
  };

  // Validation for NIC Code (max 5 characters)
  const validateNICCode = (value) => {
    if (value === "") return true;
    return value.length <= 5;
  };

  // Validation for numeric input (>= 0) - prevents typing non-numeric characters
  const validateNumeric = (value) => {
    if (value === "") return true;
    // Allow only digits and one decimal point
    if (!/^\d*\.?\d*$/.test(value)) return false;
    const num = parseFloat(value);
    if (isNaN(num)) return false;
    if (num < 0) return false;
    return true;
  };

  // Calculate locations from site data (Item 18) — scoped to reporting legal entity + site types from site creation
  const calculateLocationsFromSites = () => {
    if (!siteLocationsRaw?.length) {
      return {
        plantsNational: 0,
        plantsInternational: 0,
        officesNational: 0,
        officesInternational: 0,
      };
    }
    const entity = getReportingLegalEntity();
    if (!entity) {
      return {
        plantsNational: 0,
        plantsInternational: 0,
        officesNational: 0,
        officesInternational: 0,
      };
    }
    const agg = computeLocationAggregates(siteLocationsRaw, entity);
    return {
      plantsNational: agg.plantsNational,
      plantsInternational: agg.plantsInternational,
      officesNational: agg.officesNational,
      officesInternational: agg.officesInternational,
    };
  };

  // Calculate markets served (Item 19a) — unique states (national) / countries (international) for entity’s sites
  const calculateMarketsServed = () => {
    if (!siteLocationsRaw?.length) {
      return {
        nationalLocations: 0,
        internationalLocations: 0,
      };
    }
    const entity = getReportingLegalEntity();
    if (!entity) {
      return {
        nationalLocations: 0,
        internationalLocations: 0,
      };
    }
    const agg = computeLocationAggregates(siteLocationsRaw, entity);
    return {
      nationalLocations: agg.nationalLocations,
      internationalLocations: agg.internationalLocations,
    };
  };

  // Get calculated or manual location values
  const getLocationValues = () => {
    const hasSites = siteLocationsRaw && siteLocationsRaw.length > 0;
    if (hasSites) {
      return calculateLocationsFromSites();
    } else {
      return {
        plantsNational: operationsData.plantsNational || 0,
        plantsInternational: operationsData.plantsInternational || 0,
        officesNational: operationsData.officesNational || 0,
        officesInternational: operationsData.officesInternational || 0,
      };
    }
  };

  // Get calculated markets served values
  const getMarketsServedValues = () => {
    const hasSites = siteLocationsRaw && siteLocationsRaw.length > 0;
    if (hasSites) {
      return calculateMarketsServed();
    } else {
      return {
        nationalLocations: 0,
        internationalLocations: 0,
      };
    }
  };

  // Helper functions for Employees calculations
  const calculatePercentage = (numerator, denominator) => {
    if (!denominator || denominator === 0) return "0.00";
    const percent = (parseFloat(numerator) / parseFloat(denominator)) * 100;
    return percent.toFixed(2);
  };

  const calculateTotal = (value1, value2) => {
    return (parseFloat(value1) || 0) + (parseFloat(value2) || 0);
  };

  // Get financial years
  const getFinancialYears = () => {
    const step1 = localStorage.getItem("step1");
    let currentFY = "";
    if (step1) {
      try {
        const dataStep1 = JSON.parse(step1);
        if (dataStep1.financialYear) {
          currentFY = dataStep1.financialYear;
        }
      } catch (error) {
        console.error("Error parsing step1:", error);
      }
    }

    // Extract year from format YYYY-YY
    let currentYear = "";
    let previousYear = "";
    let priorYear = "";

    if (currentFY) {
      const parts = currentFY.split("-");
      if (parts.length === 2) {
        const startYear = parseInt(parts[0]);
        currentYear = `${startYear}-${startYear + 1}`;
        previousYear = `${startYear - 1}-${startYear}`;
        priorYear = `${startYear - 2}-${startYear - 1}`;
      }
    }

    return { currentYear, previousYear, priorYear };
  };

  // Holding Companies Functions
  const addHoldingCompanyRow = () => {
    const newId = holdingCompanies.length > 0 ? Math.max(...holdingCompanies.map((r) => r.id)) + 1 : 1;
    setHoldingCompanies([...holdingCompanies, { id: newId, name: "", type: "", sharePercentage: "", participates: "" }]);
  };

  const removeHoldingCompanyRow = (id) => {
    if (holdingCompanies.length > 1) {
      setHoldingCompanies(holdingCompanies.filter((row) => row.id !== id));
    }
  };

  const updateHoldingCompany = (id, field, value) => {
    setHoldingCompanies(holdingCompanies.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  // Material Issues Functions
  const addMaterialIssueRow = () => {
    const newId = materialIssues.length > 0 ? Math.max(...materialIssues.map((r) => r.id)) + 1 : 1;
    setMaterialIssues([...materialIssues, { id: newId, issue: "", type: "", rationale: "", approach: "", financialImplications: "" }]);
  };

  const removeMaterialIssueRow = (id) => {
    if (materialIssues.length > 1) {
      setMaterialIssues(materialIssues.filter((row) => row.id !== id));
    }
  };

  const updateMaterialIssue = (id, field, value) => {
    setMaterialIssues(materialIssues.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const businessActivitiesTotal = calculateBusinessActivitiesTotal();
  const productsServicesTotal = calculateProductsServicesTotal();
  const financialYears = getFinancialYears();

  return (
    <div>
      <Accordion className="brsr-principle-accordion">
        <AccordionTab header={<span className="principle-label">I. Details of the listed entity</span>}>
          <div className="section-a-table-container">
            <table className="section-a-table">
              <thead>
                <tr>
                  <th className="section-a-table-header">S. No.</th>
                  <th className="section-a-table-header">Field</th>
                  <th className="section-a-table-header">Input</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => (
                  <tr key={row.id} className="section-a-table-row">
                    <td className="section-a-table-label">{row.id}.</td>
                    <td className="section-a-table-label">{row.label}</td>
                    <td className="section-a-table-input">{renderInputField(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AccordionTab>

        {/* Products/Services Accordion */}
        <AccordionTab header={<span className="principle-label">II. Products/services</span>}>
          {/* Table 1: Business Activities */}
          <div className="section-a-dynamic-table-container">
            <h3 className="section-a-table-title">16. Details of business activities (accounting for 90% of the turnover)</h3>
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header">S. No.</th>
                  <th className="section-a-dynamic-table-header">Description of Main Activity</th>
                  <th className="section-a-dynamic-table-header">Description of Business Activity</th>
                  <th className="section-a-dynamic-table-header">% of Turnover of the Entity</th>
                  <th className="section-a-dynamic-table-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {businessActivities.map((row, index) => (
                  <tr key={row.id} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">{index + 1}</td>
                    <td className="section-a-dynamic-table-cell">
                      <textarea value={row.mainActivity} onChange={(e) => updateBusinessActivity(row.id, "mainActivity", e.target.value)} className="section-a-input" placeholder="Enter main activity" rows={2} />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <textarea value={row.businessActivity} onChange={(e) => updateBusinessActivity(row.id, "businessActivity", e.target.value)} className="section-a-input" placeholder="Enter business activity" rows={2} />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.turnoverPercent}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validatePercentage(value)) {
                            updateBusinessActivity(row.id, "turnoverPercent", value);
                          }
                        }}
                        className="section-a-input"
                        placeholder="0.00"
                        type="text"
                        inputMode="decimal"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {businessActivities.length > 1 && (
                        <Tooltip title="Delete" arrow>
                          <img src="/assets/img/reports/delete-repo.svg" alt="Delete Icon" className="delete-icon actions-icon" onClick={() => removeBusinessActivityRow(row.id)} style={{ cursor: "pointer" }} />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
                <tr className="section-a-dynamic-table-total-row">
                  <td colSpan="3" className="section-a-dynamic-table-total-label">
                    <strong>Total Turnover Coverage:</strong>
                  </td>
                  <td className="section-a-dynamic-table-total-value">
                    <strong>{businessActivitiesTotal.toFixed(2)}%</strong>
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
            {businessActivitiesTotal < 90 && <div className="section-a-validation-error">⚠️ Cumulative turnover coverage is {businessActivitiesTotal.toFixed(2)}%. Minimum 90% required.</div>}
            {businessActivitiesTotal > 100 && <div className="section-a-validation-error">⚠️ Cumulative turnover percentage exceeds 100% ({businessActivitiesTotal.toFixed(2)}%).</div>}
            <div className="section-a-table-actions">
              <span
                className={`section-a-add-row-text ${businessActivitiesTotal >= 100 ? "section-a-add-row-disabled" : ""}`}
                onClick={businessActivitiesTotal >= 100 ? undefined : addBusinessActivityRow}
                style={{
                  cursor: businessActivitiesTotal >= 100 ? "not-allowed" : "pointer",
                  opacity: businessActivitiesTotal >= 100 ? 0.5 : 1,
                }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>

          {/* Table 2: Products/Services */}
          <div className="section-a-dynamic-table-container" style={{ marginTop: "40px" }}>
            <h3 className="section-a-table-title">17. Products/Services sold by the entity (accounting for 90% of the entity's Turnover)</h3>
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header">S. No.</th>
                  <th className="section-a-dynamic-table-header">Product/Service</th>
                  <th className="section-a-dynamic-table-header">NIC Code</th>
                  <th className="section-a-dynamic-table-header">% of Total Turnover Contributed</th>
                  <th className="section-a-dynamic-table-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {productsServices.map((row, index) => {
                  const nicCodeError = row.nicCode && !validateNICCode(row.nicCode);
                  return (
                    <tr key={row.id} className="section-a-dynamic-table-row">
                      <td className="section-a-dynamic-table-cell">{index + 1}</td>
                      <td className="section-a-dynamic-table-cell">
                        <textarea value={row.productService} onChange={(e) => updateProductsServices(row.id, "productService", e.target.value)} className="section-a-input" placeholder="Enter product/service" rows={2} />
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <InputText
                          value={row.nicCode}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validateNICCode(value)) {
                              updateProductsServices(row.id, "nicCode", value);
                            }
                          }}
                          className={`section-a-input ${nicCodeError ? "section-a-input-error" : ""}`}
                          placeholder="Enter NIC code"
                          maxLength={5}
                        />
                        {nicCodeError && <div className="section-a-field-error">Invalid NIC Code</div>}
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <InputText
                          value={row.turnoverPercent}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              updateProductsServices(row.id, "turnoverPercent", value);
                            }
                          }}
                          className="section-a-input"
                          placeholder="0.00"
                          type="text"
                          inputMode="decimal"
                        />
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        {productsServices.length > 1 && (
                          <Tooltip title="Delete" arrow>
                            <img src="/assets/img/reports/delete-repo.svg" alt="Delete Icon" className="delete-icon actions-icon" onClick={() => removeProductsServicesRow(row.id)} style={{ cursor: "pointer" }} />
                          </Tooltip>
                        )}
                      </td>
                    </tr>
                  );
                })}
                <tr className="section-a-dynamic-table-total-row">
                  <td colSpan="3" className="section-a-dynamic-table-total-label">
                    <strong>Total Turnover Coverage:</strong>
                  </td>
                  <td className="section-a-dynamic-table-total-value">
                    <strong>{productsServicesTotal.toFixed(2)}%</strong>
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
            {productsServicesTotal < 90 && <div className="section-a-validation-error">⚠️ Cumulative turnover coverage is {productsServicesTotal.toFixed(2)}%. Minimum 90% required.</div>}
            {productsServicesTotal > 100 && <div className="section-a-validation-error">⚠️ Cumulative turnover percentage exceeds 100% ({productsServicesTotal.toFixed(2)}%).</div>}
            <div className="section-a-table-actions">
              <span
                className={`section-a-add-row-text ${productsServicesTotal >= 100 ? "section-a-add-row-disabled" : ""}`}
                onClick={productsServicesTotal >= 100 ? undefined : addProductsServicesRow}
                style={{
                  cursor: productsServicesTotal >= 100 ? "not-allowed" : "pointer",
                  opacity: productsServicesTotal >= 100 ? 0.5 : 1,
                }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </AccordionTab>

        {/* Operations Accordion */}
        <AccordionTab header={<span className="principle-label">III. Operations</span>}>
          {/* Item 18: Number of locations */}
          <div className="section-a-dynamic-table-container">
            <h3 className="section-a-table-title">18. Number of locations where plants and/or operations/offices of the entity are situated:</h3>
            {siteLocationsRaw && siteLocationsRaw.length > 0 ? (
              // Auto-populated from site locations
              <div style={{ marginTop: "16px" }}>
                <table className="section-a-dynamic-table">
                  <thead>
                    <tr>
                      <th className="section-a-dynamic-table-header">Category</th>
                      <th className="section-a-dynamic-table-header">National</th>
                      <th className="section-a-dynamic-table-header">International</th>
                      <th className="section-a-dynamic-table-header">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const locations = getLocationValues();
                      const plantsTotal = locations.plantsNational + locations.plantsInternational;
                      const officesTotal = locations.officesNational + locations.officesInternational;
                      const grandTotal = plantsTotal + officesTotal;

                      return (
                        <>
                          <tr className="section-a-dynamic-table-row">
                            <td className="section-a-dynamic-table-cell">
                              <strong>Number of plants</strong>
                            </td>
                            <td className="section-a-dynamic-table-cell">{locations.plantsNational}</td>
                            <td className="section-a-dynamic-table-cell">{locations.plantsInternational}</td>
                            <td className="section-a-dynamic-table-cell">
                              <strong>{plantsTotal}</strong>
                            </td>
                          </tr>
                          <tr className="section-a-dynamic-table-row">
                            <td className="section-a-dynamic-table-cell">
                              <strong>Number of offices</strong>
                            </td>
                            <td className="section-a-dynamic-table-cell">{locations.officesNational}</td>
                            <td className="section-a-dynamic-table-cell">{locations.officesInternational}</td>
                            <td className="section-a-dynamic-table-cell">
                              <strong>{officesTotal}</strong>
                            </td>
                          </tr>
                          <tr className="section-a-dynamic-table-total-row">
                            <td className="section-a-dynamic-table-total-label">
                              <strong>Total</strong>
                            </td>
                            <td className="section-a-dynamic-table-cell">
                              <strong>{locations.plantsNational + locations.officesNational}</strong>
                            </td>
                            <td className="section-a-dynamic-table-cell">
                              <strong>{locations.plantsInternational + locations.officesInternational}</strong>
                            </td>
                            <td className="section-a-dynamic-table-total-value">
                              <strong>{grandTotal}</strong>
                            </td>
                          </tr>
                        </>
                      );
                    })()}
                  </tbody>
                </table>
                <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>* Auto-populated from configured site locations</div>
              </div>
            ) : (
              // Manual input if sites not configured
              <div style={{ marginTop: "16px" }}>
                <table className="section-a-dynamic-table">
                  <thead>
                    <tr>
                      <th className="section-a-dynamic-table-header">Category</th>
                      <th className="section-a-dynamic-table-header">National</th>
                      <th className="section-a-dynamic-table-header">International</th>
                      <th className="section-a-dynamic-table-header">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const locations = getLocationValues();
                      const plantsTotal = (parseFloat(operationsData.plantsNational) || 0) + (parseFloat(operationsData.plantsInternational) || 0);
                      const officesTotal = (parseFloat(operationsData.officesNational) || 0) + (parseFloat(operationsData.officesInternational) || 0);
                      const grandTotal = plantsTotal + officesTotal;

                      return (
                        <>
                          <tr className="section-a-dynamic-table-row">
                            <td className="section-a-dynamic-table-cell">
                              <strong>Number of plants</strong>
                            </td>
                            <td className="section-a-dynamic-table-cell">
                              <InputText
                                value={operationsData.plantsNational}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (validateIntegersOnly(value)) {
                                    setOperationsData((prev) => ({ ...prev, plantsNational: value }));
                                  }
                                }}
                                className="section-a-input"
                                placeholder="0"
                                type="text"
                                inputMode="numeric"
                              />
                            </td>
                            <td className="section-a-dynamic-table-cell">
                              <InputText
                                value={operationsData.plantsInternational}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (validateIntegersOnly(value)) {
                                    setOperationsData((prev) => ({ ...prev, plantsInternational: value }));
                                  }
                                }}
                                className="section-a-input"
                                placeholder="0"
                                type="text"
                                inputMode="numeric"
                              />
                            </td>
                            <td className="section-a-dynamic-table-cell">
                              <strong>{plantsTotal}</strong>
                            </td>
                          </tr>
                          <tr className="section-a-dynamic-table-row">
                            <td className="section-a-dynamic-table-cell">
                              <strong>Number of offices</strong>
                            </td>
                            <td className="section-a-dynamic-table-cell">
                              <InputText
                                value={operationsData.officesNational}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (validateIntegersOnly(value)) {
                                    setOperationsData((prev) => ({ ...prev, officesNational: value }));
                                  }
                                }}
                                className="section-a-input"
                                placeholder="0"
                                type="text"
                                inputMode="numeric"
                              />
                            </td>
                            <td className="section-a-dynamic-table-cell">
                              <InputText
                                value={operationsData.officesInternational}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (validateIntegersOnly(value)) {
                                    setOperationsData((prev) => ({ ...prev, officesInternational: value }));
                                  }
                                }}
                                className="section-a-input"
                                placeholder="0"
                                type="text"
                                inputMode="numeric"
                              />
                            </td>
                            <td className="section-a-dynamic-table-cell">
                              <strong>{officesTotal}</strong>
                            </td>
                          </tr>
                          <tr className="section-a-dynamic-table-total-row">
                            <td className="section-a-dynamic-table-total-label">
                              <strong>Total</strong>
                            </td>
                            <td className="section-a-dynamic-table-cell">
                              <strong>{(parseFloat(operationsData.plantsNational) || 0) + (parseFloat(operationsData.officesNational) || 0)}</strong>
                            </td>
                            <td className="section-a-dynamic-table-cell">
                              <strong>{(parseFloat(operationsData.plantsInternational) || 0) + (parseFloat(operationsData.officesInternational) || 0)}</strong>
                            </td>
                            <td className="section-a-dynamic-table-total-value">
                              <strong>{grandTotal}</strong>
                            </td>
                          </tr>
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Item 19: Markets served by the entity */}
          <div className="section-a-dynamic-table-container" style={{ marginTop: "40px" }}>
            <h3 className="section-a-table-title">19. Markets served by the entity</h3>

            {/* 19a: Number of locations */}
            <div style={{ marginTop: "16px" }}>
              <h3 className="section-a-table-title">a. Number of locations</h3>
              {siteLocationsRaw && siteLocationsRaw.length > 0 ? (
                <table className="section-a-dynamic-table">
                  <thead>
                    <tr>
                      <th className="section-a-dynamic-table-header">Locations</th>
                      <th className="section-a-dynamic-table-header">Number</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const markets = getMarketsServedValues();
                      return (
                        <>
                          <tr className="section-a-dynamic-table-row">
                            <td className="section-a-dynamic-table-cell">National (No. of States)</td>
                            <td className="section-a-dynamic-table-cell">{markets.nationalLocations}</td>
                          </tr>
                          <tr className="section-a-dynamic-table-row">
                            <td className="section-a-dynamic-table-cell">International (No. of Countries)</td>
                            <td className="section-a-dynamic-table-cell">{markets.internationalLocations}</td>
                          </tr>
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              ) : (
                <div style={{ fontSize: "12px", color: "#666", padding: "12px" }}>No site locations configured. Please configure site locations to auto-populate this data.</div>
              )}
            </div>

            {/* 19b: Exports percentage */}
            <div style={{ marginTop: "40px" }}>
              <h3 className="section-a-table-title">b. What is the contribution of exports as a percentage of the total turnover of the entity?</h3>
              <div style={{ maxWidth: "300px" }}>
                <InputText
                  value={operationsData.exportsPercentage}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (validatePercentage(value)) {
                      setOperationsData((prev) => ({ ...prev, exportsPercentage: value }));
                    }
                  }}
                  className="section-a-input"
                  placeholder="0.00"
                  type="text"
                  inputMode="decimal"
                />
                <div style={{ marginTop: "4px", fontSize: "12px", color: "#666" }}>Enter value between 0 and 100 (up to 2 decimal places)</div>
              </div>
            </div>

            {/* 19c: Customer types */}
            <div style={{ marginTop: "40px" }}>
              <h3 className="section-a-table-title">c. A brief on types of customers</h3>
              <div>
                <textarea
                  value={operationsData.customerTypes}
                  onChange={(e) => {
                    setOperationsData((prev) => ({ ...prev, customerTypes: e.target.value }));
                  }}
                  className="section-a-input"
                  placeholder="Enter a brief description of the types of customers served by the entity..."
                  rows={4}
                />
              </div>
            </div>
          </div>
        </AccordionTab>

        {/* Employees Accordion */}
        <AccordionTab header={<span className="principle-label">IV. Employees</span>}>
          {/* Item 20a: Employees and workers */}
          <div className="section-a-dynamic-table-container">
            <h3 className="section-a-table-title">20. Details as at the end of Financial Year:</h3>
            <h3 className="section-a-table-title" style={{ marginTop: "16px" }}>
              a. Employees and workers (including differently abled):
            </h3>

            <div style={{ marginTop: "0" }}>
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header" rowSpan="2">
                      S. No.
                    </th>
                    <th className="section-a-dynamic-table-header" rowSpan="2">
                      Particulars
                    </th>
                    <th className="section-a-dynamic-table-header" rowSpan="2">
                      Total (A)
                    </th>
                    <th className="section-a-dynamic-table-header" colSpan="2">
                      Male
                    </th>
                    <th className="section-a-dynamic-table-header" colSpan="2">
                      Female
                    </th>
                  </tr>
                  <tr>
                    <th className="section-a-dynamic-table-header">No. (B)</th>
                    <th className="section-a-dynamic-table-header">% (B/A)</th>
                    <th className="section-a-dynamic-table-header">No. (C)</th>
                    <th className="section-a-dynamic-table-header">% (C/A)</th>
                  </tr>
                </thead>
                <tbody>
                  {/* EMPLOYEES Section */}
                  <tr style={{ backgroundColor: "#f5f5f5" }}>
                    <td colSpan="7" style={{ fontWeight: "bold", padding: "8px 12px" }}>
                      EMPLOYEES
                    </td>
                  </tr>
                  {/* Row 1: Permanent Employees */}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">1.</td>
                    <td className="section-a-dynamic-table-cell">Permanent (D)</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.empPermanentTotal}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, empPermanentTotal: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.empPermanentMale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, empPermanentMale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.empPermanentMale, employeesData.empPermanentTotal)}%</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.empPermanentFemale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, empPermanentFemale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.empPermanentFemale, employeesData.empPermanentTotal)}%</td>
                  </tr>
                  {/* Row 2: Other than Permanent Employees */}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">2.</td>
                    <td className="section-a-dynamic-table-cell">Other than Permanent (E)</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.empOtherTotal}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, empOtherTotal: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.empOtherMale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, empOtherMale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.empOtherMale, employeesData.empOtherTotal)}%</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.empOtherFemale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, empOtherFemale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.empOtherFemale, employeesData.empOtherTotal)}%</td>
                  </tr>
                  {/* Row 3: Total Employees */}
                  <tr className="section-a-dynamic-table-total-row">
                    <td className="section-a-dynamic-table-cell">3.</td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>Total employees (D + E)</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculateTotal(employeesData.empPermanentTotal, employeesData.empOtherTotal)}</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculateTotal(employeesData.empPermanentMale, employeesData.empOtherMale)}</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculatePercentage(calculateTotal(employeesData.empPermanentMale, employeesData.empOtherMale), calculateTotal(employeesData.empPermanentTotal, employeesData.empOtherTotal))}%</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculateTotal(employeesData.empPermanentFemale, employeesData.empOtherFemale)}</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculatePercentage(calculateTotal(employeesData.empPermanentFemale, employeesData.empOtherFemale), calculateTotal(employeesData.empPermanentTotal, employeesData.empOtherTotal))}%</strong>
                    </td>
                  </tr>

                  {/* WORKERS Section */}
                  <tr style={{ backgroundColor: "#f5f5f5" }}>
                    <td colSpan="7" style={{ fontWeight: "bold", padding: "8px 12px" }}>
                      WORKERS
                    </td>
                  </tr>
                  {/* Row 4: Permanent Workers */}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">4.</td>
                    <td className="section-a-dynamic-table-cell">Permanent (F)</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.workerPermanentTotal}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, workerPermanentTotal: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.workerPermanentMale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, workerPermanentMale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.workerPermanentMale, employeesData.workerPermanentTotal)}%</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.workerPermanentFemale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, workerPermanentFemale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.workerPermanentFemale, employeesData.workerPermanentTotal)}%</td>
                  </tr>
                  {/* Row 5: Other than Permanent Workers */}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">5.</td>
                    <td className="section-a-dynamic-table-cell">Other than Permanent (G)</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.workerOtherTotal}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, workerOtherTotal: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.workerOtherMale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, workerOtherMale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.workerOtherMale, employeesData.workerOtherTotal)}%</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.workerOtherFemale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, workerOtherFemale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.workerOtherFemale, employeesData.workerOtherTotal)}%</td>
                  </tr>
                  {/* Row 6: Total Workers */}
                  <tr className="section-a-dynamic-table-total-row">
                    <td className="section-a-dynamic-table-cell">6.</td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>Total workers (F + G)</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculateTotal(employeesData.workerPermanentTotal, employeesData.workerOtherTotal)}</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculateTotal(employeesData.workerPermanentMale, employeesData.workerOtherMale)}</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculatePercentage(calculateTotal(employeesData.workerPermanentMale, employeesData.workerOtherMale), calculateTotal(employeesData.workerPermanentTotal, employeesData.workerOtherTotal))}%</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculateTotal(employeesData.workerPermanentFemale, employeesData.workerOtherFemale)}</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculatePercentage(calculateTotal(employeesData.workerPermanentFemale, employeesData.workerOtherFemale), calculateTotal(employeesData.workerPermanentTotal, employeesData.workerOtherTotal))}%</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Item 20b: Differently abled */}
          <div className="section-a-dynamic-table-container" style={{ marginTop: "40px" }}>
            <h3 className="section-a-table-title">b. Differently abled Employees and workers</h3>

            <div style={{ marginTop: "0" }}>
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header" rowSpan="2">
                      S. No.
                    </th>
                    <th className="section-a-dynamic-table-header" rowSpan="2">
                      Particulars
                    </th>
                    <th className="section-a-dynamic-table-header" rowSpan="2">
                      Total (A)
                    </th>
                    <th className="section-a-dynamic-table-header" colSpan="2">
                      Male
                    </th>
                    <th className="section-a-dynamic-table-header" colSpan="2">
                      Female
                    </th>
                  </tr>
                  <tr>
                    <th className="section-a-dynamic-table-header">No. (B)</th>
                    <th className="section-a-dynamic-table-header">% (B/A)</th>
                    <th className="section-a-dynamic-table-header">No. (C)</th>
                    <th className="section-a-dynamic-table-header">% (C/A)</th>
                  </tr>
                </thead>
                <tbody>
                  {/* DIFFERENTLY ABLED EMPLOYEES Section */}
                  <tr style={{ backgroundColor: "#f5f5f5" }}>
                    <td colSpan="7" style={{ fontWeight: "bold", padding: "8px 12px" }}>
                      DIFFERENTLY ABLED EMPLOYEES
                    </td>
                  </tr>
                  {/* Row 1: Permanent */}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">1.</td>
                    <td className="section-a-dynamic-table-cell">Permanent (D)</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.diffAbledEmpPermanentTotal}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, diffAbledEmpPermanentTotal: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.diffAbledEmpPermanentMale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, diffAbledEmpPermanentMale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.diffAbledEmpPermanentMale, employeesData.diffAbledEmpPermanentTotal)}%</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.diffAbledEmpPermanentFemale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, diffAbledEmpPermanentFemale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.diffAbledEmpPermanentFemale, employeesData.diffAbledEmpPermanentTotal)}%</td>
                  </tr>
                  {/* Row 2: Other than Permanent */}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">2.</td>
                    <td className="section-a-dynamic-table-cell">Other than Permanent (E)</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.diffAbledEmpOtherTotal}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, diffAbledEmpOtherTotal: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.diffAbledEmpOtherMale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, diffAbledEmpOtherMale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.diffAbledEmpOtherMale, employeesData.diffAbledEmpOtherTotal)}%</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.diffAbledEmpOtherFemale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, diffAbledEmpOtherFemale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.diffAbledEmpOtherFemale, employeesData.diffAbledEmpOtherTotal)}%</td>
                  </tr>
                  {/* Row 3: Total */}
                  <tr className="section-a-dynamic-table-total-row">
                    <td className="section-a-dynamic-table-cell">3.</td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>Total differently abled employees (D + E)</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculateTotal(employeesData.diffAbledEmpPermanentTotal, employeesData.diffAbledEmpOtherTotal)}</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculateTotal(employeesData.diffAbledEmpPermanentMale, employeesData.diffAbledEmpOtherMale)}</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculatePercentage(calculateTotal(employeesData.diffAbledEmpPermanentMale, employeesData.diffAbledEmpOtherMale), calculateTotal(employeesData.diffAbledEmpPermanentTotal, employeesData.diffAbledEmpOtherTotal))}%</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculateTotal(employeesData.diffAbledEmpPermanentFemale, employeesData.diffAbledEmpOtherFemale)}</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculatePercentage(calculateTotal(employeesData.diffAbledEmpPermanentFemale, employeesData.diffAbledEmpOtherFemale), calculateTotal(employeesData.diffAbledEmpPermanentTotal, employeesData.diffAbledEmpOtherTotal))}%</strong>
                    </td>
                  </tr>

                  {/* DIFFERENTLY ABLED WORKERS Section */}
                  <tr style={{ backgroundColor: "#f5f5f5" }}>
                    <td colSpan="7" style={{ fontWeight: "bold", padding: "8px 12px" }}>
                      DIFFERENTLY ABLED WORKERS
                    </td>
                  </tr>
                  {/* Row 4: Permanent */}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">4.</td>
                    <td className="section-a-dynamic-table-cell">Permanent (F)</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.diffAbledWorkerPermanentTotal}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, diffAbledWorkerPermanentTotal: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.diffAbledWorkerPermanentMale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, diffAbledWorkerPermanentMale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.diffAbledWorkerPermanentMale, employeesData.diffAbledWorkerPermanentTotal)}%</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.diffAbledWorkerPermanentFemale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, diffAbledWorkerPermanentFemale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.diffAbledWorkerPermanentFemale, employeesData.diffAbledWorkerPermanentTotal)}%</td>
                  </tr>
                  {/* Row 5: Other than Permanent */}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">5.</td>
                    <td className="section-a-dynamic-table-cell">Other than permanent (G)</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.diffAbledWorkerOtherTotal}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, diffAbledWorkerOtherTotal: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.diffAbledWorkerOtherMale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, diffAbledWorkerOtherMale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.diffAbledWorkerOtherMale, employeesData.diffAbledWorkerOtherTotal)}%</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.diffAbledWorkerOtherFemale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, diffAbledWorkerOtherFemale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.diffAbledWorkerOtherFemale, employeesData.diffAbledWorkerOtherTotal)}%</td>
                  </tr>
                  {/* Row 6: Total */}
                  <tr className="section-a-dynamic-table-total-row">
                    <td className="section-a-dynamic-table-cell">6.</td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>Total differently abled workers (F + G)</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculateTotal(employeesData.diffAbledWorkerPermanentTotal, employeesData.diffAbledWorkerOtherTotal)}</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculateTotal(employeesData.diffAbledWorkerPermanentMale, employeesData.diffAbledWorkerOtherMale)}</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculatePercentage(calculateTotal(employeesData.diffAbledWorkerPermanentMale, employeesData.diffAbledWorkerOtherMale), calculateTotal(employeesData.diffAbledWorkerPermanentTotal, employeesData.diffAbledWorkerOtherTotal))}%</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculateTotal(employeesData.diffAbledWorkerPermanentFemale, employeesData.diffAbledWorkerOtherFemale)}</strong>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <strong>{calculatePercentage(calculateTotal(employeesData.diffAbledWorkerPermanentFemale, employeesData.diffAbledWorkerOtherFemale), calculateTotal(employeesData.diffAbledWorkerPermanentTotal, employeesData.diffAbledWorkerOtherTotal))}%</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Item 21: Women participation */}
          <div className="section-a-dynamic-table-container" style={{ marginTop: "40px" }}>
            <h3 className="section-a-table-title">21. Participation/Inclusion/Representation of women</h3>

            <div style={{ marginTop: "16px" }}>
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header">S. No.</th>
                    <th className="section-a-dynamic-table-header">Particulars</th>
                    <th className="section-a-dynamic-table-header">Total (A)</th>
                    <th className="section-a-dynamic-table-header">No. (B)</th>
                    <th className="section-a-dynamic-table-header">% (B/A)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">1.</td>
                    <td className="section-a-dynamic-table-cell">Board of Directors</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.womenBoardTotal}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, womenBoardTotal: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.womenBoardCount}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, womenBoardCount: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.womenBoardCount, employeesData.womenBoardTotal)}%</td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">2.</td>
                    <td className="section-a-dynamic-table-cell">Senior Management</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.womenSeniorTotal}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, womenSeniorTotal: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.womenSeniorCount}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, womenSeniorCount: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.womenSeniorCount, employeesData.womenSeniorTotal)}%</td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">3.</td>
                    <td className="section-a-dynamic-table-cell">Middle Management</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.womenMiddleTotal}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, womenMiddleTotal: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.womenMiddleCount}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, womenMiddleCount: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.womenMiddleCount, employeesData.womenMiddleTotal)}%</td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">4.</td>
                    <td className="section-a-dynamic-table-cell">Junior Management</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.womenJuniorTotal}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, womenJuniorTotal: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.womenJuniorCount}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setEmployeesData((prev) => ({ ...prev, womenJuniorCount: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculatePercentage(employeesData.womenJuniorCount, employeesData.womenJuniorTotal)}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Item 22: Turnover rate */}
          <div className="section-a-dynamic-table-container" style={{ marginTop: "40px" }}>
            <h3 className="section-a-table-title">22. Turnover rate for permanent employees and workers</h3>

            <div style={{ marginTop: "16px" }}>
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header" rowSpan="2"></th>
                    <th className="section-a-dynamic-table-header" colSpan="3">
                      FY {financialYears.currentYear || "____"}
                      <br />
                      (Turnover rate in current FY)
                    </th>
                    <th className="section-a-dynamic-table-header" colSpan="3">
                      FY {financialYears.previousYear || "____"}
                      <br />
                      (Turnover rate in previous FY)
                    </th>
                    <th className="section-a-dynamic-table-header" colSpan="3">
                      FY {financialYears.priorYear || "____"}
                      <br />
                      (Turnover rate in the year prior to the previous FY)
                    </th>
                  </tr>
                  <tr>
                    <th className="section-a-dynamic-table-header">Male</th>
                    <th className="section-a-dynamic-table-header">Female</th>
                    <th className="section-a-dynamic-table-header">Total</th>
                    <th className="section-a-dynamic-table-header">Male</th>
                    <th className="section-a-dynamic-table-header">Female</th>
                    <th className="section-a-dynamic-table-header">Total</th>
                    <th className="section-a-dynamic-table-header">Male</th>
                    <th className="section-a-dynamic-table-header">Female</th>
                    <th className="section-a-dynamic-table-header">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      <strong>Permanent Employees</strong>
                    </td>
                    {/* Current FY */}
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.turnoverEmpCurrentMale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumeric(value)) {
                            setEmployeesData((prev) => ({ ...prev, turnoverEmpCurrentMale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.turnoverEmpCurrentFemale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumeric(value)) {
                            setEmployeesData((prev) => ({ ...prev, turnoverEmpCurrentFemale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculateTotal(employeesData.turnoverEmpCurrentMale, employeesData.turnoverEmpCurrentFemale)}</td>
                    {/* Previous FY */}
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.turnoverEmpPreviousMale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumeric(value)) {
                            setEmployeesData((prev) => ({ ...prev, turnoverEmpPreviousMale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.turnoverEmpPreviousFemale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumeric(value)) {
                            setEmployeesData((prev) => ({ ...prev, turnoverEmpPreviousFemale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculateTotal(employeesData.turnoverEmpPreviousMale, employeesData.turnoverEmpPreviousFemale)}</td>
                    {/* Prior to Previous FY */}
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.turnoverEmpPriorMale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumeric(value)) {
                            setEmployeesData((prev) => ({ ...prev, turnoverEmpPriorMale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.turnoverEmpPriorFemale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumeric(value)) {
                            setEmployeesData((prev) => ({ ...prev, turnoverEmpPriorFemale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculateTotal(employeesData.turnoverEmpPriorMale, employeesData.turnoverEmpPriorFemale)}</td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      <strong>Permanent Workers</strong>
                    </td>
                    {/* Current FY */}
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.turnoverWorkerCurrentMale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumeric(value)) {
                            setEmployeesData((prev) => ({ ...prev, turnoverWorkerCurrentMale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.turnoverWorkerCurrentFemale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumeric(value)) {
                            setEmployeesData((prev) => ({ ...prev, turnoverWorkerCurrentFemale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculateTotal(employeesData.turnoverWorkerCurrentMale, employeesData.turnoverWorkerCurrentFemale)}</td>
                    {/* Previous FY */}
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.turnoverWorkerPreviousMale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumeric(value)) {
                            setEmployeesData((prev) => ({ ...prev, turnoverWorkerPreviousMale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.turnoverWorkerPreviousFemale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumeric(value)) {
                            setEmployeesData((prev) => ({ ...prev, turnoverWorkerPreviousFemale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculateTotal(employeesData.turnoverWorkerPreviousMale, employeesData.turnoverWorkerPreviousFemale)}</td>
                    {/* Prior to Previous FY */}
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.turnoverWorkerPriorMale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumeric(value)) {
                            setEmployeesData((prev) => ({ ...prev, turnoverWorkerPriorMale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={employeesData.turnoverWorkerPriorFemale}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumeric(value)) {
                            setEmployeesData((prev) => ({ ...prev, turnoverWorkerPriorFemale: value }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{calculateTotal(employeesData.turnoverWorkerPriorMale, employeesData.turnoverWorkerPriorFemale)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </AccordionTab>

        {/* Holding/Subsidiary Companies Accordion */}
        <AccordionTab header={<span className="principle-label">V. Holding, Subsidiary and Associate Companies (including joint ventures)</span>}>
          <div className="section-a-dynamic-table-container">
            <h3 className="section-a-table-title">23. (a) Names of holding / subsidiary / associate companies / joint ventures</h3>

            <div style={{ marginTop: "16px" }}>
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header">S. No.</th>
                    <th className="section-a-dynamic-table-header">Name of the holding / subsidiary / associate companies / joint ventures (A)</th>
                    <th className="section-a-dynamic-table-header">Indicate whether holding/ Subsidiary/ Associate/ Joint Venture</th>
                    <th className="section-a-dynamic-table-header">% of shares held by listed entity</th>
                    <th className="section-a-dynamic-table-header">Does the entity indicated at column A, participate in the Business Responsibility initiatives of the listed entity? (Yes/No)</th>
                    <th className="section-a-dynamic-table-header">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {holdingCompanies.map((row, index) => (
                    <tr key={row.id} className="section-a-dynamic-table-row">
                      <td className="section-a-dynamic-table-cell">{index + 1}</td>
                      <td className="section-a-dynamic-table-cell">
                        <textarea value={row.name} onChange={(e) => updateHoldingCompany(row.id, "name", e.target.value)} className="section-a-input" placeholder="Enter company name" rows={2} />
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <Dropdown
                          value={row.type}
                          options={[
                            { label: "Holding", value: "holding" },
                            { label: "Subsidiary", value: "subsidiary" },
                            { label: "Associate company", value: "associate" },
                            { label: "Joint Venture", value: "jointventure" },
                          ]}
                          onChange={(e) => updateHoldingCompany(row.id, "type", e.value || "")}
                          placeholder="Select type"
                          className="section-a-dropdown"
                        />
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <InputText
                          value={row.sharePercentage}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              updateHoldingCompany(row.id, "sharePercentage", value);
                            }
                          }}
                          className="section-a-input"
                          placeholder="0.00"
                          type="text"
                          inputMode="decimal"
                        />
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <div style={{ display: "flex", gap: "20px", alignItems: "center", justifyContent: "center" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <RadioButton inputId={`participates-yes-${row.id}`} value="yes" checked={row.participates === "yes"} onChange={(e) => updateHoldingCompany(row.id, "participates", "yes")} />
                            <label htmlFor={`participates-yes-${row.id}`} style={{ cursor: "pointer", margin: 0 }}>
                              Yes
                            </label>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <RadioButton inputId={`participates-no-${row.id}`} value="no" checked={row.participates === "no"} onChange={(e) => updateHoldingCompany(row.id, "participates", "no")} />
                            <label htmlFor={`participates-no-${row.id}`} style={{ cursor: "pointer", margin: 0 }}>
                              No
                            </label>
                          </div>
                        </div>
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        {holdingCompanies.length > 1 && (
                          <Tooltip title="Delete" arrow>
                            <img src="/assets/img/reports/delete-repo.svg" alt="Delete Icon" className="delete-icon actions-icon" onClick={() => removeHoldingCompanyRow(row.id)} style={{ cursor: "pointer" }} />
                          </Tooltip>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="section-a-table-actions">
                <span className="section-a-add-row-text" onClick={addHoldingCompanyRow}>
                  <span className="section-a-add-icon">+</span> Add Row
                </span>
              </div>
            </div>
          </div>
        </AccordionTab>

        {/* CSR Accordion */}
        <AccordionTab header={<span className="principle-label">VI. CSR</span>}>
          <div className="section-a-dynamic-table-container">
            <h3 className="section-a-table-title">24. (i) Whether CSR is applicable as per section 135 of Companies Act, 2013:</h3>
            <div style={{ marginTop: "16px", maxWidth: "300px" }}>
              <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <RadioButton inputId="csr-applicable-yes" value="yes" checked={csrData.csrApplicable === "yes"} onChange={() => setCsrData((prev) => ({ ...prev, csrApplicable: "yes" }))} />
                  <label htmlFor="csr-applicable-yes" style={{ cursor: "pointer", margin: 0 }}>
                    Yes
                  </label>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <RadioButton inputId="csr-applicable-no" value="no" checked={csrData.csrApplicable === "no"} onChange={() => setCsrData((prev) => ({ ...prev, csrApplicable: "no", csrPolicyWebLink: "" }))} />
                  <label htmlFor="csr-applicable-no" style={{ cursor: "pointer", margin: 0 }}>
                    No
                  </label>
                </div>
              </div>
            </div>

            {csrData.csrApplicable === "yes" && (
              <div style={{ marginTop: "20px", maxWidth: "720px" }}>
                <label htmlFor="csr-applicable-details" className="formlable" style={{ display: "block", marginBottom: "8px" }}>
                  If yes, provide details:
                </label>
                <InputTextarea id="csr-applicable-details" value={csrData.csrPolicyWebLink || ""} onChange={(e) => setCsrData((prev) => ({ ...prev, csrPolicyWebLink: e.target.value }))} rows={4} className="section-a-input" style={{ width: "100%" }} placeholder="Enter your answer" autoResize />
              </div>
            )}

            <div className="section-a-dynamic-table-container" style={{ marginTop: "40px" }}>
              <h3 className="section-a-table-title">24. (ii) Turnover (in Rs.)</h3>
              <div style={{ marginTop: "16px", maxWidth: "400px" }}>
                <div className="section-a-paidup-capital-group">
                  <Dropdown value="INR" options={[{ label: "Rs.", value: "INR" }]} disabled className="section-a-dropdown section-a-currency" style={{ width: "40%", marginRight: "8px" }} />
                  <InputText
                    value={csrData.turnover}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        setCsrData((prev) => ({ ...prev, turnover: value }));
                      }
                    }}
                    className="section-a-input"
                    placeholder="Enter turnover"
                    type="text"
                    style={{ width: "58%" }}
                  />
                </div>
                <div style={{ marginTop: "4px", fontSize: "12px", color: "#666" }}>Example: if turnover is 2 lakhs, mention 200000</div>
              </div>
            </div>

            <div className="section-a-dynamic-table-container" style={{ marginTop: "40px" }}>
              <h3 className="section-a-table-title">24. (iii) Net worth (in Rs.)</h3>
              <div style={{ marginTop: "16px", maxWidth: "400px" }}>
                <div className="section-a-paidup-capital-group">
                  <Dropdown value="INR" options={[{ label: "Rs.", value: "INR" }]} disabled className="section-a-dropdown section-a-currency" style={{ width: "40%", marginRight: "8px" }} />
                  <InputText
                    value={csrData.netWorth}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        setCsrData((prev) => ({ ...prev, netWorth: value }));
                      }
                    }}
                    className="section-a-input"
                    placeholder="Enter net worth"
                    type="text"
                    style={{ width: "58%" }}
                  />
                </div>
                <div style={{ marginTop: "4px", fontSize: "12px", color: "#666" }}>Example: if net worth is 2 lakhs, mention 200000</div>
              </div>
            </div>
          </div>
        </AccordionTab>

        {/* Transparency and Disclosures Accordion */}
        <AccordionTab header={<span className="principle-label">VII. Transparency and Disclosures Compliances</span>}>
          {/* Item 25: Complaints/Grievances */}
          <div className="section-a-dynamic-table-container section-a-dynamic-table-container-responsive">
            <h3 className="section-a-table-title">25. Complaints/Grievances on any of the principles (Principles 1 to 9) under the National Guidelines on Responsible Business Conduct:</h3>

            <div style={{ marginTop: "16px" }}>
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header" rowSpan="2">
                      Stakeholder group from whom complaint is received
                    </th>
                    <th className="section-a-dynamic-table-header" rowSpan="2">
                      Grievance Redressal Mechanism in Place (Yes/No)
                    </th>
                    <th className="section-a-dynamic-table-header" rowSpan="2">
                      (If Yes, then provide web-link for grievance redress policy)
                    </th>
                    <th className="section-a-dynamic-table-header" colSpan="3">
                      FY {financialYears.currentYear || "____"} Current Financial Year
                    </th>
                    <th className="section-a-dynamic-table-header" colSpan="3">
                      FY {financialYears.previousYear || "____"} Previous Financial Year
                    </th>
                  </tr>
                  <tr>
                    <th className="section-a-dynamic-table-header">Number of complaints filed during the year</th>
                    <th className="section-a-dynamic-table-header">Number of complaints pending resolution at close of the year</th>
                    <th className="section-a-dynamic-table-header">Remarks</th>
                    <th className="section-a-dynamic-table-header">Number of complaints filed during the year</th>
                    <th className="section-a-dynamic-table-header">Number of complaints pending resolution at close of the year</th>
                    <th className="section-a-dynamic-table-header">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(complaintsData).map(([key, data]) => (
                    <tr key={key} className="section-a-dynamic-table-row">
                      <td className="section-a-dynamic-table-cell">
                        {key === "other" ? (
                          <InputText
                            value={data.otherSpecify || ""}
                            onChange={(e) => {
                              setComplaintsData((prev) => ({
                                ...prev,
                                [key]: { ...prev[key], otherSpecify: e.target.value },
                              }));
                            }}
                            className="section-a-input"
                            placeholder="Other (please specify)"
                          />
                        ) : (
                          key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1")
                        )}
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <div style={{ display: "flex", gap: "20px", alignItems: "center", justifyContent: "center" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <RadioButton
                              inputId={`mechanism-yes-${key}`}
                              value="yes"
                              checked={data.mechanism === "yes"}
                              onChange={(e) => {
                                setComplaintsData((prev) => ({
                                  ...prev,
                                  [key]: { ...prev[key], mechanism: "yes" },
                                }));
                              }}
                            />
                            <label htmlFor={`mechanism-yes-${key}`} style={{ cursor: "pointer", margin: 0 }}>
                              Yes
                            </label>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <RadioButton
                              inputId={`mechanism-no-${key}`}
                              value="no"
                              checked={data.mechanism === "no"}
                              onChange={(e) => {
                                setComplaintsData((prev) => ({
                                  ...prev,
                                  [key]: { ...prev[key], mechanism: "no" },
                                }));
                              }}
                            />
                            <label htmlFor={`mechanism-no-${key}`} style={{ cursor: "pointer", margin: 0 }}>
                              No
                            </label>
                          </div>
                        </div>
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        {data.mechanism === "yes" ? (
                          <InputText
                            value={data.webLink || ""}
                            onChange={(e) => {
                              setComplaintsData((prev) => ({
                                ...prev,
                                [key]: { ...prev[key], webLink: e.target.value },
                              }));
                            }}
                            className="section-a-input"
                            placeholder="Enter web link"
                          />
                        ) : (
                          <span style={{ color: "#999" }}>—</span>
                        )}
                      </td>
                      {/* Current FY */}
                      <td className="section-a-dynamic-table-cell">
                        <InputText
                          value={data.currentFiled || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validateNumeric(value)) {
                              setComplaintsData((prev) => ({
                                ...prev,
                                [key]: { ...prev[key], currentFiled: value },
                              }));
                            }
                          }}
                          className="section-a-input"
                          placeholder="0"
                          type="number"
                          min="0"
                        />
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <InputText
                          value={data.currentPending || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validateNumeric(value)) {
                              setComplaintsData((prev) => ({
                                ...prev,
                                [key]: { ...prev[key], currentPending: value },
                              }));
                            }
                          }}
                          className="section-a-input"
                          placeholder="0"
                          type="number"
                          min="0"
                        />
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <textarea
                          value={data.currentRemarks || ""}
                          onChange={(e) => {
                            setComplaintsData((prev) => ({
                              ...prev,
                              [key]: { ...prev[key], currentRemarks: e.target.value },
                            }));
                          }}
                          className="section-a-input"
                          placeholder="Enter remarks"
                          rows={2}
                        />
                      </td>
                      {/* Previous FY */}
                      <td className="section-a-dynamic-table-cell">
                        <InputText
                          value={data.previousFiled || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validateNumeric(value)) {
                              setComplaintsData((prev) => ({
                                ...prev,
                                [key]: { ...prev[key], previousFiled: value },
                              }));
                            }
                          }}
                          className="section-a-input"
                          placeholder="0"
                          type="number"
                          min="0"
                        />
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <InputText
                          value={data.previousPending || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validateNumeric(value)) {
                              setComplaintsData((prev) => ({
                                ...prev,
                                [key]: { ...prev[key], previousPending: value },
                              }));
                            }
                          }}
                          className="section-a-input"
                          placeholder="0"
                          type="number"
                          min="0"
                        />
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <textarea
                          value={data.previousRemarks || ""}
                          onChange={(e) => {
                            setComplaintsData((prev) => ({
                              ...prev,
                              [key]: { ...prev[key], previousRemarks: e.target.value },
                            }));
                          }}
                          className="section-a-input"
                          placeholder="Enter remarks"
                          rows={2}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Item 26: Material Issues */}
          <div className="section-a-dynamic-table-container" style={{ marginTop: "40px" }}>
            <h3 className="section-a-table-title">26. Overview of the entity's material responsible business conduct issues</h3>

            <div style={{ marginTop: "16px" }}>
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header">S. No.</th>
                    <th className="section-a-dynamic-table-header">Material issue identified</th>
                    <th className="section-a-dynamic-table-header">Indicate whether risk or opportunity (R/O)</th>
                    <th className="section-a-dynamic-table-header">Rationale for identifying the risk / opportunity</th>
                    <th className="section-a-dynamic-table-header">In case of risk, approach to adapt or mitigate</th>
                    <th className="section-a-dynamic-table-header">Financial implications of the risk or opportunity (Indicate positive or negative implications)</th>
                    <th className="section-a-dynamic-table-header">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {materialIssues.map((row, index) => (
                    <tr key={row.id} className="section-a-dynamic-table-row">
                      <td className="section-a-dynamic-table-cell">{index + 1}</td>
                      <td className="section-a-dynamic-table-cell">
                        <textarea value={row.issue} onChange={(e) => updateMaterialIssue(row.id, "issue", e.target.value)} className="section-a-input" placeholder="Enter material issue" rows={2} />
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <Dropdown
                          value={row.type}
                          options={[
                            { label: "Risk", value: "risk" },
                            { label: "Opportunity", value: "opportunity" },
                          ]}
                          onChange={(e) => updateMaterialIssue(row.id, "type", e.value || "")}
                          placeholder="Select"
                          className="section-a-dropdown"
                        />
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <textarea value={row.rationale} onChange={(e) => updateMaterialIssue(row.id, "rationale", e.target.value)} className="section-a-input" placeholder="Enter rationale" rows={2} />
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <textarea value={row.approach} onChange={(e) => updateMaterialIssue(row.id, "approach", e.target.value)} className="section-a-input" placeholder="Enter approach" rows={2} />
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <textarea value={row.financialImplications} onChange={(e) => updateMaterialIssue(row.id, "financialImplications", e.target.value)} className="section-a-input" placeholder="Enter financial implications" rows={2} />
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        {materialIssues.length > 1 && (
                          <Tooltip title="Delete" arrow>
                            <img src="/assets/img/reports/delete-repo.svg" alt="Delete Icon" className="delete-icon actions-icon" onClick={() => removeMaterialIssueRow(row.id)} style={{ cursor: "pointer" }} />
                          </Tooltip>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="section-a-table-actions">
                <span className="section-a-add-row-text" onClick={addMaterialIssueRow}>
                  <span className="section-a-add-icon">+</span> Add Row
                </span>
              </div>
            </div>
          </div>
        </AccordionTab>
      </Accordion>
    </div>
  );
};

export default SectionA;
